import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { TranslationService } from '../../../core/i18n/translation.service';
import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { AuditDto } from '../models/audit.models';
import { AuditsApi } from '../services/audits.api';

type AuditViewMode = 'list' | 'tiles';

const AUDIT_VIEW_STORAGE_KEY = 'junoaudit.audits.view-mode';
const AUDIT_PROCESSING_POLL_INTERVAL_MS = 3000;

interface LoadAuditsOptions {
  silent?: boolean;
}

@Component({
  selector: 'app-audit-list',
  imports: [CommonModule, DatePipe, ConfirmationDialogComponent],
  templateUrl: './audit-list.component.html',
  styleUrl: './audit-list.component.css',
})
export class AuditListComponent {
  private readonly api = inject(AuditsApi);
  private readonly destroyRef = inject(DestroyRef);
  private loadRequestId = 0;
  private processingPollIntervalId: number | null = null;

  protected readonly i18n = inject(TranslationService);

  readonly isActive = input(false);
  readonly searchQuery = input('');
  readonly editRequested = output<string>();

  protected readonly isLoading = signal(false);
  protected readonly loadError = signal('');
  protected readonly audits = signal<AuditDto[]>([]);
  protected readonly viewMode = signal<AuditViewMode>(this.restoreViewMode());
  protected readonly deleteDialogOpen = signal(false);
  protected readonly auditPendingDeletion = signal<AuditDto | null>(null);
  protected readonly isDeleting = signal(false);

  constructor() {
    effect(() => {
      if (this.isActive()) {
        void this.loadAudits();
        return;
      }

      this.stopProcessingPolling();
    });

    this.destroyRef.onDestroy(() => {
      this.stopProcessingPolling();
    });
  }

  protected setViewMode(mode: AuditViewMode): void {
    if (this.viewMode() === mode) {
      return;
    }

    this.viewMode.set(mode);
    localStorage.setItem(AUDIT_VIEW_STORAGE_KEY, mode);
  }

  protected trackByAuditId(_index: number, audit: AuditDto): string {
    return audit.id;
  }

  protected getStatusLabel(audit: AuditDto): string {
    return this.i18n.t(`auditList.status.${audit.status}`);
  }

  protected getStatusClass(audit: AuditDto): Record<string, boolean> {
    return {
      'app-status-chip--draft': audit.status === 'draft',
      'app-status-chip--active': audit.status === 'active',
      'app-status-chip--scheduled': audit.status === 'scheduled',
      'app-status-chip--archived': audit.status === 'archived',
      'audit-list__status-chip--processing': audit.status === 'processing',
      'audit-list__status-chip--failed': audit.status === 'failed',
    };
  }

  protected getChecklistCount(audit: AuditDto): number {
    return audit.checklists.length;
  }

  protected getVisibleAudits(): AuditDto[] {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return this.audits();
    }

    return this.audits().filter((audit) => audit.name.toLowerCase().includes(query));
  }

  protected editAudit(audit: AuditDto): void {
    this.editRequested.emit(audit.id);
  }

  protected deleteAudit(audit: AuditDto): void {
    this.auditPendingDeletion.set(audit);
    this.deleteDialogOpen.set(true);
  }

  protected cancelAuditDeletion(): void {
    if (this.isDeleting()) {
      return;
    }

    this.closeDeleteDialog();
  }

  protected async confirmAuditDeletion(): Promise<void> {
    const audit = this.auditPendingDeletion();
    if (!audit || this.isDeleting()) {
      return;
    }

    this.isDeleting.set(true);
    this.loadError.set('');

    try {
      await firstValueFrom(this.api.deleteAudit(audit.id));
      const remainingAudits = this.audits().filter((currentAudit) => currentAudit.id !== audit.id);
      this.audits.set(remainingAudits);
      this.syncProcessingPolling(remainingAudits);
      this.closeDeleteDialog();
    } catch {
      this.loadError.set(this.i18n.t('auditList.errors.delete'));
    } finally {
      this.isDeleting.set(false);
    }
  }

  private async loadAudits(options: LoadAuditsOptions = {}): Promise<void> {
    const requestId = ++this.loadRequestId;
    const silent = options.silent === true;

    if (!silent) {
      this.isLoading.set(true);
      this.loadError.set('');
    }

    try {
      const results = await firstValueFrom(this.api.getAudits());
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.audits.set(results.results);
      this.syncProcessingPolling(results.results);
    } catch {
      if (requestId !== this.loadRequestId) {
        return;
      }
      if (!silent) {
        this.loadError.set(this.i18n.t('auditList.errors.load'));
      }
    } finally {
      if (!silent && requestId === this.loadRequestId) {
        this.isLoading.set(false);
      }
    }
  }

  private restoreViewMode(): AuditViewMode {
    const savedMode = localStorage.getItem(AUDIT_VIEW_STORAGE_KEY);
    return savedMode === 'tiles' ? 'tiles' : 'list';
  }

  private closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
    this.auditPendingDeletion.set(null);
  }

  private syncProcessingPolling(audits: AuditDto[]): void {
    if (!this.isActive()) {
      this.stopProcessingPolling();
      return;
    }

    const hasProcessingAudit = audits.some((audit) => audit.status === 'processing');
    if (hasProcessingAudit) {
      this.startProcessingPolling();
      return;
    }

    this.stopProcessingPolling();
  }

  private startProcessingPolling(): void {
    if (this.processingPollIntervalId !== null) {
      return;
    }

    this.processingPollIntervalId = window.setInterval(() => {
      if (!this.isActive() || this.isLoading() || this.isDeleting()) {
        return;
      }

      void this.loadAudits({ silent: true });
    }, AUDIT_PROCESSING_POLL_INTERVAL_MS);
  }

  private stopProcessingPolling(): void {
    if (this.processingPollIntervalId === null) {
      return;
    }

    window.clearInterval(this.processingPollIntervalId);
    this.processingPollIntervalId = null;
  }
}
